import { useEffect, useMemo, useRef, useState } from "react";
import { BreadCrumb } from "primereact/breadcrumb";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Toast } from "primereact/toast";
import { ConfirmPopup } from "primereact/confirmpopup";
import { Paginator } from "primereact/paginator";
import { Skeleton } from "primereact/skeleton";
import { useMediaQuery } from "react-responsive";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import MeaPage from "../../components/MeaPage";
import MeaLink from "../../components/MeaLink";
import { useAuthContext } from "../../hooks/useAuthContext";
import trainingsAPI from "../../services/trainings";
import accountsAPI from "../../services/accounts";
import studentsAPI from "../../services/students";
import classesAPI from "../../services/classes";
import FamilyInfoModal from "../registrations/components/FamilyInfoModal";
import { useStudentObservationActions } from "../trainings/students/components/useStudentObservationActions";
import {
  formatObservationDate,
  ObservationApproval,
  ObservationResponseDialog,
  ObservationVisibility,
} from "../trainings/students/components/StudentObservationCells";
import { PERMISSION_TRAINING_STUDENT_OBSERVATIONS } from "../../permissions";

export const STUDENT_OBSERVATIONS_SECTION = "student-observations";

const STATUS_FILTERS = [
  { value: "", label: "Toutes", statKey: "total", icon: "pi pi-list" },
  { value: "internal", label: "Internes", statKey: "internal", icon: "pi pi-lock" },
  { value: "pending", label: "En attente de lecture", statKey: "pending", icon: "pi pi-clock" },
  { value: "approved", label: "Lues par la famille", statKey: "approved", icon: "pi pi-check" },
  { value: "with_response", label: "Avec réponse", statKey: "with_response", icon: "pi pi-envelope" },
  { value: "email_failed", label: "Email non envoyé", statKey: "email_failed", icon: "pi pi-exclamation-triangle" },
];

const pagePath = (schoolId, trainingId) =>
  `/school/${schoolId}/admin/${STUDENT_OBSERVATIONS_SECTION}${trainingId ? `/${trainingId}` : ""}`;

const SchoolStudentObservationsPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { hasPermission, auth, currentMembership, updateCurrentMembershipPreferences } = useAuthContext();
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const toast = useRef(null);
  const familyInfoModalRef = useRef(null);

  const schoolId = params.schoolId || currentMembership?.school?.id;
  const selectedTrainingId = params.trainingId || currentMembership?.preferences?.last_visited_training;
  const accessToken = auth?.access_token || "";
  const canList = hasPermission(PERMISSION_TRAINING_STUDENT_OBSERVATIONS.LIST);
  const { hasActions, renderActions } = useStudentObservationActions({
    schoolId,
    trainingId: selectedTrainingId,
    accessToken,
    toast,
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState("");
  const [classId, setClassId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [responseObservation, setResponseObservation] = useState(null);
  const [loadingStudentId, setLoadingStudentId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedObservationId = searchParams.get("observation") || "";
  const autoOpenedObservationRef = useRef("");

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [status, classId, search, selectedTrainingId, focusedObservationId]);

  useEffect(() => {
    setClassId("");
  }, [selectedTrainingId]);

  const trainingsQuery = trainingsAPI.useGetList(schoolId, accessToken, 1, 1000, "");
  const trainings = useMemo(() => trainingsQuery?.data?.data || [], [trainingsQuery?.data]);
  const selectedTraining = trainings.find((training) => training.id === selectedTrainingId) || null;
  const activeTrainingId = canList && selectedTraining ? selectedTrainingId : undefined;

  const classesQuery = classesAPI.useGetList(schoolId, activeTrainingId, accessToken, 1, 1000, {});
  const classOptions = [
    { label: "Toutes les classes", value: "" },
    ...(classesQuery?.data?.data || []).map((item) => ({ label: item.name, value: item.id })),
  ];

  const observationsQuery = studentsAPI.ListTrainingObservations(
    schoolId,
    activeTrainingId,
    accessToken,
    page,
    pageSize,
    {
      status,
      search,
      class_id: classId,
      observation_id: focusedObservationId,
    },
  );
  const observations = useMemo(() => observationsQuery?.data?.data || [], [observationsQuery?.data]);
  const totalRecords = observationsQuery?.data?.total || 0;
  const stats = observationsQuery?.data?.extraMeta || {};

  // Link from the "read by the family" email: open the family response right away.
  useEffect(() => {
    if (!focusedObservationId || autoOpenedObservationRef.current === focusedObservationId) {
      return;
    }
    const focused = observations.find((item) => item.id === focusedObservationId);
    if (focused) {
      autoOpenedObservationRef.current = focusedObservationId;
      if (focused.approval_response) {
        setResponseObservation(focused);
      }
    }
  }, [focusedObservationId, observations]);

  const clearFocusedObservation = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("observation");
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    if (!schoolId || trainings.length === 0) {
      return;
    }
    if (!selectedTrainingId || !trainings.some((training) => training.id === selectedTrainingId)) {
      const fallbackTrainingId = currentMembership?.preferences?.last_visited_training || trainings[0].id;
      if (fallbackTrainingId && fallbackTrainingId !== selectedTrainingId) {
        navigate(pagePath(schoolId, fallbackTrainingId), { replace: true });
      }
    }
  }, [schoolId, selectedTrainingId, trainings, currentMembership, navigate]);

  const handleTrainingChange = async (nextTrainingId) => {
    if (!nextTrainingId) {
      return;
    }
    try {
      if (currentMembership?.id) {
        const updatedPrefs = await accountsAPI.UpdateMemberPreferences(
          accessToken,
          currentMembership.id,
          nextTrainingId,
        );
        updateCurrentMembershipPreferences(
          updatedPrefs || { ...(currentMembership?.preferences || {}), last_visited_training: nextTrainingId },
        );
      }
    } catch (err) {
      console.error("Failed to save training preference", err);
    }
    navigate(pagePath(schoolId, nextTrainingId), { replace: true });
  };

  const sectionOptions = [
    { label: "Inscriptions", value: "candidates" },
    { label: "Paiements", value: "payments" },
    { label: "Campagnes d'e-mails", value: "campaigns" },
    { label: "Gestion de l'espace famille", value: "settings" },
    { label: "Avis de passage", value: "avis-passage" },
    { label: "Changements de créneau", value: "slot-change-requests" },
    { label: "Suivi des observations", value: STUDENT_OBSERVATIONS_SECTION },
  ];

  const handleSectionChange = (nextSection) => {
    if (!nextSection || nextSection === STUDENT_OBSERVATIONS_SECTION) {
      return;
    }
    if (nextSection === "avis-passage" || nextSection === "slot-change-requests") {
      navigate(`/school/${schoolId}/admin/${nextSection}/${selectedTrainingId}`);
      return;
    }
    navigate(`/school/${schoolId}/admin/registrations/${selectedTrainingId}/${nextSection}`);
  };

  const breadcrumb = [
    {
      template: () => (
        <MeaLink to={`/school/${schoolId}/admin`} icon="pi pi-home" isRipple={true}>
          {currentMembership?.school?.name || "Accueil"}
        </MeaLink>
      ),
    },
    { label: "Suivi des observations" },
  ];
  if (selectedTraining) {
    breadcrumb.push({ label: selectedTraining.name });
  }

  const customWorkspaceBar = (
    <div className="w-full p-breadcrumb border-bottom-1 surface-border py-2 px-3 flex align-items-center flex-wrap gap-2">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-th-large text-teal-600 text-sm"></i>
        <Dropdown
          value={STUDENT_OBSERVATIONS_SECTION}
          options={sectionOptions}
          onChange={(e) => handleSectionChange(e.value)}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
          aria-label="Section"
        />
      </div>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <div className="flex align-items-center gap-2">
        <i className="pi pi-bookmark text-indigo-500 text-sm"></i>
        <Dropdown
          value={selectedTrainingId}
          options={trainings}
          optionLabel="name"
          optionValue="id"
          onChange={(e) => handleTrainingChange(e.value)}
          placeholder={selectedTraining?.name || "Sélectionner une formation"}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
          aria-label="Formation"
        />
      </div>
    </div>
  );

  if (!canList) {
    return (
      <MeaPage title="Suivi des observations" breadcrumb={<BreadCrumb model={breadcrumb} />}>
        <Message severity="warn" text="Vous n'avez pas accès au suivi des observations." />
      </MeaPage>
    );
  }

  if (!selectedTrainingId || !selectedTraining) {
    return (
      <MeaPage title="Suivi des observations" breadcrumb={<BreadCrumb model={breadcrumb} />}>
        <Message severity="info" text="Aucune formation sélectionnée." />
      </MeaPage>
    );
  }

  const openStudent = async (student) => {
    if (!student?.id) {
      return;
    }
    setLoadingStudentId(student.id);
    try {
      const result = await studentsAPI.FetchOne(schoolId, selectedTrainingId, accessToken, student.id);
      familyInfoModalRef.current?.show(result?.data || result);
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Erreur", detail: err.message, life: 4000 });
    } finally {
      setLoadingStudentId(null);
    }
  };

  const renderStudent = (item) => (
    <Button
      label={item?.student?.name || "-"}
      link
      className="p-0 text-left"
      loading={loadingStudentId === item?.student?.id}
      onClick={() => openStudent(item?.student)}
    />
  );

  const renderMessage = (item) => (
    <div
      title={item?.message}
      style={{
        whiteSpace: "pre-line",
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        maxWidth: isMobile ? "100%" : "32rem",
      }}
    >
      {item?.message}
    </div>
  );

  const handlePage = (e) => {
    setPageSize(e.rows);
    setPage(Math.floor(e.first / e.rows) + 1);
  };

  const isLoadingList = observationsQuery?.isLoading || observationsQuery?.isFetching;

  const renderCards = () => {
    if (observationsQuery?.isLoading) {
      return (
        <div className="flex flex-column gap-2">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} height="8rem" className="border-round" />
          ))}
        </div>
      );
    }
    if (observations.length === 0) {
      return <div className="text-center text-500 py-4">Aucune observation.</div>;
    }
    return (
      <div className="flex flex-column gap-2" data-testid="observations-cards">
        {observations.map((item) => (
          <div
            key={item.id}
            className="surface-card shadow-1 border-round p-3 flex flex-column gap-2"
            data-testid={`observation-card-${item.id}`}
          >
            <div className="flex justify-content-between align-items-start gap-2">
              <div className="flex flex-column">
                {renderStudent(item)}
                <small className="text-600">{item?.student?.classe?.name || "-"}</small>
              </div>
              <small className="text-600 white-space-nowrap">{formatObservationDate(item?.issued_at)}</small>
            </div>
            {renderMessage(item)}
            <small className="text-600">
              <i className="pi pi-user text-xs mr-1" />
              {item?.author?.name || "-"}
            </small>
            <div className="flex flex-wrap align-items-start justify-content-between gap-2 border-top-1 surface-border pt-2">
              <ObservationVisibility observation={item} />
              <ObservationApproval observation={item} onShowResponse={setResponseObservation} />
            </div>
            {hasActions(item) && <div className="flex justify-content-end">{renderActions(item)}</div>}
          </div>
        ))}
        {totalRecords > pageSize && (
          <Paginator
            first={(page - 1) * pageSize}
            rows={pageSize}
            totalRecords={totalRecords}
            onPageChange={handlePage}
            template="PrevPageLink CurrentPageReport NextPageLink"
            currentPageReportTemplate="{currentPage} / {totalPages}"
          />
        )}
      </div>
    );
  };

  return (
    <MeaPage
      title="Suivi des observations"
      subtitle={`Toutes les observations des élèves — ${selectedTraining.name}`}
      breadcrumb={customWorkspaceBar}
    >
      <Toast ref={toast} />
      <ConfirmPopup />
      <div className="flex flex-wrap gap-2 mb-3">
        {STATUS_FILTERS.map((item) => (
          <Button
            key={item.value || "all"}
            label={`${item.label} (${stats?.[item.statKey] ?? 0})`}
            icon={item.icon}
            size="small"
            rounded
            outlined={status !== item.value}
            severity={item.value === "email_failed" ? "danger" : undefined}
            onClick={() => setStatus(item.value)}
            aria-pressed={status === item.value}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <IconField iconPosition="left" className={isMobile ? "w-full" : undefined}>
          <InputIcon className="pi pi-search" />
          <InputText
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Rechercher (élève, message)"
            className="w-full"
            style={isMobile ? undefined : { width: "22rem" }}
            aria-label="Rechercher"
          />
        </IconField>
        <Dropdown
          value={classId}
          options={classOptions}
          onChange={(e) => setClassId(e.value || "")}
          placeholder="Toutes les classes"
          filter
          className={isMobile ? "w-full" : undefined}
          aria-label="Classe"
        />
      </div>
      {focusedObservationId && (
        <div className="flex flex-wrap align-items-center gap-2 mb-3 p-2 border-round surface-100">
          <i className="pi pi-filter text-primary" aria-hidden="true" />
          <span className="text-sm">Affichage limité à l'observation ouverte depuis la notification.</span>
          <Button
            label="Voir toutes les observations"
            icon="pi pi-list"
            size="small"
            text
            onClick={clearFocusedObservation}
          />
        </div>
      )}
      {observationsQuery?.isError && (
        <Message
          severity="error"
          className="mb-3"
          text={observationsQuery?.error?.message || "Impossible de récupérer les observations."}
        />
      )}
      {isMobile ? (
        renderCards()
      ) : (
        <DataTable
          value={observations}
          dataKey="id"
          lazy
          paginator
          first={(page - 1) * pageSize}
          rows={pageSize}
          totalRecords={totalRecords}
          rowsPerPageOptions={[25, 50, 100]}
          onPage={handlePage}
          loading={isLoadingList}
          className="p-datatable-sm"
          emptyMessage="Aucune observation."
          responsiveLayout="scroll"
        >
          <Column
            header="Date"
            body={(item) => formatObservationDate(item?.issued_at)}
            style={{ whiteSpace: "nowrap" }}
          />
          <Column header="Élève" body={renderStudent} />
          <Column header="Classe" body={(item) => item?.student?.classe?.name || "-"} />
          <Column header="Observation" body={renderMessage} />
          <Column header="Auteur" body={(item) => item?.author?.name || "-"} />
          <Column header="Visibilité" body={(item) => <ObservationVisibility observation={item} />} />
          <Column
            header="Prise de connaissance"
            body={(item) => <ObservationApproval observation={item} onShowResponse={setResponseObservation} />}
          />
          <Column
            header="Actions"
            body={(item) => (hasActions(item) ? renderActions(item) : null)}
            style={{ whiteSpace: "nowrap" }}
          />
        </DataTable>
      )}
      <ObservationResponseDialog observation={responseObservation} onHide={() => setResponseObservation(null)} />
      <FamilyInfoModal ref={familyInfoModalRef} schoolId={schoolId} trainingId={selectedTrainingId} />
    </MeaPage>
  );
};

export default SchoolStudentObservationsPage;
